/**
 * The pure determination function (§8).
 *
 * Non-negotiable properties, each with a test:
 *  1. Pure — no I/O, no network, no filesystem, no model calls, no randomness.
 *  2. Never reads the clock — `facts.as_of` is a required input.
 *  3. Byte-identical replay — see serialize.ts.
 *  4. Every conclusion carries its chain (`because`), and every rule its citation.
 *  5. Fails closed — unknown facts produce PENDING_EVIDENCE, never an assumed MET.
 *  6. Out-of-scope short-circuits — never partially determine a case we cannot handle.
 */
import type { Figure, Rule, Ruleset } from "@verity/rules";
import type {
  CaseFacts,
  Determination,
  ISODate,
  RequirementResult,
  RuleId,
} from "./types.js";
import { compareISODate, isValidISODate, maxISODate } from "./dates.js";
import { ENGINE_VERSION } from "./version.js";
import { appliesPredicates, evaluatePredicates } from "./predicates/index.js";
import type { PredicateContext } from "./predicates/predicate.js";

export function determine(facts: CaseFacts, ruleset: Ruleset): Determination {
  if (!facts || typeof facts.as_of !== "string" || !isValidISODate(facts.as_of)) {
    throw new Error(
      "facts.as_of is required and must be an ISO date — the engine never reads the clock (§8.2)",
    );
  }

  const base = {
    ruleset_version: ruleset.version,
    engine_version: ENGINE_VERSION,
    as_of: facts.as_of,
  };

  // 6. Out-of-scope short-circuits: evaluate nothing else (prohibition 4).
  const flagged = (
    Object.keys(facts.out_of_scope_flags) as Array<keyof CaseFacts["out_of_scope_flags"]>
  ).filter((flag) => facts.out_of_scope_flags[flag] === true);

  if (flagged.length > 0) {
    const referrals = flagged.map((flag) => {
      const referral = ruleset.out_of_scope[flag];
      if (!referral) throw new Error(`Ruleset has no out-of-scope referral for flag: ${flag}`);
      return referral;
    });
    const first = referrals[0];
    if (!first) throw new Error("unreachable: flagged is non-empty");
    return {
      ...base,
      verdict: "OUT_OF_SCOPE",
      requirements: [],
      out_of_scope: {
        reasons: referrals.map((r) => r.reason),
        referral_hint: first.referral_hint,
        flags: flagged,
      },
    };
  }

  const active = ruleset.rules.filter(
    (rule) => rule.stages.includes(facts.stage) && effectiveAt(rule, facts.as_of),
  );
  const ordered = dependencyOrder(active);

  const results = new Map<RuleId, RequirementResult>();
  const ctx = makeContext(ruleset, results, facts.as_of);

  for (const rule of ordered) {
    if (rule.applies_when !== null) {
      const applies = appliesPredicates[rule.applies_when];
      if (!applies) throw new Error(`Unknown applies_when predicate: ${rule.applies_when}`);
      if (!applies(facts, rule, ctx)) {
        results.set(rule.id, resultFor(rule, { status: "NOT_APPLICABLE" }));
        continue;
      }
    }
    const evaluate = evaluatePredicates[rule.evaluate];
    if (!evaluate) throw new Error(`Unknown evaluate predicate: ${rule.evaluate}`);
    results.set(rule.id, resultFor(rule, evaluate(facts, rule, ctx)));
  }

  const requirements = ordered
    .map((rule) => results.get(rule.id))
    .filter((r): r is RequirementResult => r !== undefined);

  // NOT_READY: something is not met YET, but only because a period has not
  // elapsed — and the engine can say when it will (NR-1).
  //
  // PENDING_EVIDENCE deliberately does NOT prevent this verdict. Awaiting a
  // document is the normal state of every real case; if it blocked NOT_READY,
  // the computed date — "you qualify on 14 March 2027", the most generous
  // thing this product says — would never be reached once the evidence and
  // format rules are in the ruleset. A requirement that is NOT_MET for a
  // reason time cannot fix is different: that keeps the verdict IN_SCOPE with
  // the failure visible, because a date would be misleading.
  const notMet = requirements.filter((r) => r.status === "NOT_MET");
  const timeBlocked = notMet.filter((r) => r.eligible_from !== undefined);
  const blockedForOtherReasons = notMet.filter((r) => r.eligible_from === undefined);

  if (timeBlocked.length > 0 && blockedForOtherReasons.length === 0) {
    return {
      ...base,
      verdict: "NOT_READY",
      requirements,
      not_ready: {
        eligible_from: maxISODate(timeBlocked.map((r) => r.eligible_from as ISODate)),
        blocking: timeBlocked.map((r) => r.rule_id),
      },
    };
  }

  return { ...base, verdict: "IN_SCOPE", requirements };
}

function resultFor(
  rule: Rule,
  outcome: {
    status: RequirementResult["status"];
    figures?: Record<string, number>;
    value?: string;
    eligible_from?: ISODate;
    dates?: Record<string, ISODate>;
    notes?: string[];
    because?: RuleId[];
  },
): RequirementResult {
  const because = [...new Set([...(outcome.because ?? []), ...rule.depends_on])];
  const result: RequirementResult = {
    rule_id: rule.id,
    status: outcome.status,
    plain_english: rule.plain_english,
    citation: rule.citation,
    evidence_required: rule.evidence,
    because,
  };
  if (outcome.figures !== undefined) result.figures = outcome.figures;
  if (outcome.value !== undefined) result.value = outcome.value;
  if (outcome.eligible_from !== undefined) result.eligible_from = outcome.eligible_from;
  if (outcome.dates !== undefined) result.dates = outcome.dates;
  if (outcome.notes !== undefined) result.notes = outcome.notes;
  return result;
}

function effectiveAt(rule: Rule, asOf: ISODate): boolean {
  if (compareISODate(asOf, rule.effective_from) < 0) return false;
  return rule.effective_to === null || compareISODate(asOf, rule.effective_to) <= 0;
}

function figureEffectiveAt(figure: Figure, asOf: ISODate): boolean {
  if (compareISODate(asOf, figure.effective_from) < 0) return false;
  return figure.effective_to === null || compareISODate(asOf, figure.effective_to) <= 0;
}

function makeContext(
  ruleset: Ruleset,
  results: ReadonlyMap<RuleId, RequirementResult>,
  asOf: ISODate,
): PredicateContext {
  const figure = (id: string): number | string => {
    const entry = ruleset.figures[id];
    if (!entry) {
      throw new Error(`Ruleset has no figure "${id}" — every number must trace to the figures register`);
    }
    if (!figureEffectiveAt(entry, asOf)) {
      throw new Error(`Figure "${id}" is not effective at ${asOf}`);
    }
    return entry.value;
  };
  const numericFigure = (id: string): number => {
    const value = figure(id);
    if (typeof value !== "number") throw new Error(`Figure "${id}" is not numeric`);
    return value;
  };
  const paramFigure = (rule: Rule, param: string): number | string => {
    const figureId = rule.params?.[param];
    if (typeof figureId !== "string") {
      throw new Error(`Rule ${rule.id} has no "${param}" param naming a figure`);
    }
    return figure(figureId);
  };
  return { ruleset, results, figure, numericFigure, paramFigure };
}

/**
 * Stable dependency order: rules evaluate after everything in their
 * depends_on, ties broken by ruleset order. A cycle or a dangling
 * dependency is a data error and throws.
 */
function dependencyOrder(rules: Rule[]): Rule[] {
  const byId = new Map(rules.map((r) => [r.id, r]));
  const ordered: Rule[] = [];
  const state = new Map<RuleId, "visiting" | "done">();

  const visit = (rule: Rule): void => {
    const s = state.get(rule.id);
    if (s === "done") return;
    if (s === "visiting") throw new Error(`Dependency cycle involving rule ${rule.id}`);
    state.set(rule.id, "visiting");
    for (const dep of rule.depends_on) {
      const depRule = byId.get(dep);
      // A dependency outside the active stage set is legitimate (it simply
      // produced no result); only same-set dependencies order evaluation.
      if (depRule) visit(depRule);
    }
    state.set(rule.id, "done");
    ordered.push(rule);
  };

  for (const rule of rules) visit(rule);
  return ordered;
}
